import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MessageCircle, MessageSquare } from "lucide-react";

// Global unread-message bell - purely presentational/controlled: the
// summary (and the polling that produces it) lives in DashboardLayout.js,
// which renders this twice at once (desktop fixed position + mobile top
// bar) - polling here too would double-fetch and double-trigger the
// new-message ribbon/tone there. Clicking a thread always routes into the
// appointment ledger (/appointment?open_messages=<id>), never straight into
// a new consultation - starting a new consultation is date-gated to the
// appointment's own day (see Appointment.js::handleConsultation), so a
// notification about a pre-visit message must never be blocked by that gate.
// Follow-up reminders (ConsultationFollowUpModel) are a separate notification
// type with their own icon/bell entirely - see FollowUpBell.js - deliberately
// not merged into this one, so the two are never confused at a glance.
export default function NotificationBell({ summary = { total_unread: 0, threads: [] }, iconClassName = "" }) {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const goToThread = (appointmentId) => {
    setIsOpen(false);
    navigate(`/appointment?open_messages=${appointmentId}`);
  };

  const totalUnread = summary.total_unread || 0;

  return (
    <div className="relative" ref={containerRef}>
      <button
        onClick={() => setIsOpen((v) => !v)}
        className={`relative p-2 rounded-xl text-secondary-500 hover:bg-primary-50 hover:text-primary-600 transition-all ${iconClassName}`}
        title="Messages"
      >
        <MessageCircle size={18} />
        {totalUnread > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center leading-none">
            {totalUnread > 9 ? "9+" : totalUnread}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-72 bg-white rounded-xl shadow-xl ring-1 ring-black/5 overflow-hidden z-50 animate-in fade-in slide-in-from-top-2 duration-150">
          <div className="px-3 py-2 border-b border-secondary-100 flex items-center justify-between">
            <span className="text-xs font-bold text-secondary-700">Patient Messages</span>
            {totalUnread > 0 && (
              <span className="text-[10px] font-semibold text-rose-600">{totalUnread} unread</span>
            )}
          </div>
          <div className="max-h-80 overflow-y-auto">
            {summary.threads.length === 0 ? (
              <p className="px-3 py-4 text-xs text-secondary-400 text-center">No unread messages</p>
            ) : (
              summary.threads.map((t) => (
                <button
                  key={t.appointment_id}
                  onClick={() => goToThread(t.appointment_id)}
                  className="w-full text-left px-3 py-2.5 hover:bg-primary-50 transition-colors border-b border-secondary-50 last:border-0"
                >
                  <div className="flex items-start gap-2">
                    <MessageSquare size={14} className="text-primary-500 mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-xs font-semibold text-secondary-800 truncate">{t.patient_name || "Patient"}</p>
                        <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-rose-100 text-rose-700 flex-shrink-0">
                          {t.unread_count}
                        </span>
                      </div>
                      <p className="text-[11px] text-secondary-400 truncate">{t.last_message_preview}</p>
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
